/**
 * Centralized video URL source detection.
 *
 * Why: lectures' Summary Video URL column historically held a YouTube link.
 * For lectures whose summary video is generated/uploaded manually to Drive,
 * the column will now hold a Google Drive share link instead. This helper
 * tells the caller WHICH player to render so we don't need YouTube-specific
 * code paths in every consumer.
 *
 * Supported Drive URL forms (covers what `Share → Copy link` produces today):
 *   https://drive.google.com/file/d/<ID>/view              (most common)
 *   https://drive.google.com/file/d/<ID>/view?usp=sharing
 *   https://drive.google.com/file/d/<ID>/preview
 *   https://drive.google.com/open?id=<ID>
 *   https://drive.google.com/uc?export=...&id=<ID>
 *
 * If a future format appears, extend DRIVE_ID_PATTERNS rather than the
 * detector itself — the detector stays declarative.
 */

import { extractVideoId } from './youtubeUtils';
import { buildDriveStreamUrl } from './driveStreamUrl';

const DRIVE_ID_PATTERNS = [
  /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]{10,})/i,   // /file/d/<ID>/...
  /drive\.google\.com\/open\?[^#]*\bid=([a-zA-Z0-9_-]{10,})/i,
  /drive\.google\.com\/uc\?[^#]*\bid=([a-zA-Z0-9_-]{10,})/i,
];

/**
 * Extract a Drive file ID from any of the supported URL forms.
 * Returns null if the URL is not a recognised Drive link.
 */
export function extractDriveFileId(url) {
  if (!url || typeof url !== 'string') return null;
  for (const re of DRIVE_ID_PATTERNS) {
    const m = url.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
}

/**
 * Detect the source kind of a video URL.
 * Returns an object the caller can switch on:
 *   { kind: 'youtube', id }       — render via <YouTubeEmbed />
 *   { kind: 'drive',   id, streamUrl } — render via <DriveVideoEmbed />
 *   { kind: 'none',    url }      — empty/invalid
 *
 * YouTube takes precedence (some Drive URLs technically contain youtu.be in
 * the description text but the file URL itself never does; this just keeps
 * the common case fast).
 */
export function detectVideoSource(url) {
  if (!url || typeof url !== 'string') {
    return { kind: 'none', url: '' };
  }

  const ytId = extractVideoId(url);
  if (ytId) {
    return { kind: 'youtube', id: ytId };
  }

  const driveId = extractDriveFileId(url);
  if (driveId) {
    return {
      kind: 'drive',
      id: driveId,
      streamUrl: buildDriveStreamUrl(driveId),
    };
  }

  return { kind: 'none', url };
}

export default detectVideoSource;
